'use client';

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import { GitCompare } from 'lucide-react';
import { CertificationCard } from '@/components/landing/CertificationCard';
import { CertificationListItem, WeightProfileListItem, api } from '@/lib/api';
import { fadeInUp } from '@/lib/animations';
import { SearchSortBar, SortBy, SortDirection } from '@/components/ui';

export default function CertificationsShowcasePage() {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const [certifications, setCertifications] = useState<CertificationListItem[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [weightProfiles, setWeightProfiles] = useState<WeightProfileListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('alphabetical');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length < 3) return [...prev, id];
      return prev;
    });
  }, []);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => {
      if (prev) {
        setSelectedIds([]);
        sessionStorage.removeItem('ceau_compare_selection');
      }
      return !prev;
    });
  }, []);

  // Persist selection to sessionStorage whenever it changes
  useEffect(() => {
    if (selectionMode && selectedIds.length > 0) {
      sessionStorage.setItem('ceau_compare_selection', selectedIds.join(','));
    }
  }, [selectedIds, selectionMode]);

  // Restore selection only when returning from compare page
  useEffect(() => {
    const fromCompare = sessionStorage.getItem('ceau_compare_back');
    if (fromCompare) {
      sessionStorage.removeItem('ceau_compare_back');
      const stored = sessionStorage.getItem('ceau_compare_selection');
      if (stored) {
        const storedIds = stored.split(',').filter(Boolean);
        if (storedIds.length >= 2) {
          setSelectedIds(storedIds);
          setSelectionMode(true);
        }
      }
    } else {
      sessionStorage.removeItem('ceau_compare_selection');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [certList, countryList, profileList] = await Promise.all([
          api.getCertifications().catch(() => []),
          api.getCountries().catch(() => []),
          api.getWeightProfiles().catch(() => [])
        ]);

        setCertifications((certList || []).filter((c: CertificationListItem) => c.status === 'certified'));
        setCountries(countryList || []);
        setWeightProfiles(profileList || []);
      } catch (error) {
        console.error('Error fetching certifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const getCountryName = (code: string) => {
    const country = countries.find(c => c.code === code);
    return country ? (locale === 'pt' ? country.name_pt : country.name_en) : code;
  };

  const getMainImage = (cert: CertificationListItem) => {
    return cert.main_image_url || null;
  };

  const getWeightProfileName = (cert: CertificationListItem) => {
    if (!cert.weight_profile_id) return '';
    const profile = weightProfiles.find(p => p.id === cert.weight_profile_id);
    return profile ? (locale === 'pt' ? profile.name_pt : profile.name_en) : '';
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSortChange = useCallback((by: SortBy, direction: SortDirection) => {
    setSortBy(by);
    setSortDirection(direction);
  }, []);

  const filteredCertifications = useMemo(() => {
    let result = [...certifications];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item =>
        (item.name?.toLowerCase().includes(query)) ||
        (item.site_city?.toLowerCase().includes(query)) ||
        (getCountryName(item.site_country || '')?.toLowerCase().includes(query)) ||
        (item.offsite_city?.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === 'alphabetical') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortDirection === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else {
        // For 'updated', sort by updated_at date
        const dateA = new Date(a.updated_at).getTime();
        const dateB = new Date(b.updated_at).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });

    return result;
  }, [certifications, searchQuery, sortBy, sortDirection, countries]);

  return (
    <main className="min-h-screen bg-[#F9F9F9]">
      <section className="pt-24 pb-20 px-6 md:px-10">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1 className="text-3xl font-medium tracking-tight text-black mb-1">
                {t('landing.certificationsSection.title')}
              </h1>
              <p className="text-[#888888]">
                {filteredCertifications.length === 1
                  ? t('landing.certificationsSection.count', { count: 1 })
                  : t('landing.certificationsSection.countPlural', { count: filteredCertifications.length })}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {selectionMode && selectedIds.length >= 2 && (
                <button
                  onClick={() => {
                    sessionStorage.setItem('ceau_compare_selection', selectedIds.join(','));
                    router.push(`/compare?ids=${selectedIds.join(',')}`);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-black text-white hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <GitCompare className="w-4 h-4" />
                  Compare ({selectedIds.length})
                </button>
              )}
              <button
                onClick={toggleSelectionMode}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                  selectionMode
                    ? 'bg-black text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                title="Select certifications to compare"
              >
                <GitCompare className="w-4 h-4" />
                {selectionMode ? 'Cancel' : 'Select'}
              </button>
              <SearchSortBar
                searchValue={searchQuery}
                onSearchChange={handleSearchChange}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                showUpdatedSort={true}
              />
            </div>
          </div>

          {!isLoading && filteredCertifications.length > 0 ? (
            <motion.div
              key={`${searchQuery}-${sortBy}-${sortDirection}`}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="initial"
              animate="animate"
              variants={{
                initial: { opacity: 0 },
                animate: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05
                  }
                }
              }}
            >
              {filteredCertifications.map((cert: CertificationListItem) => {
                const isSelected = selectedIds.includes(cert.id);
                const atMax = selectedIds.length >= 3;
                const canToggle = isSelected || !atMax;
                return (
                  <motion.div
                    key={cert.id}
                    className="h-[280px] relative"
                    variants={{
                      initial: { opacity: 0, y: 20 },
                      animate: { opacity: 1, y: 0 }
                    }}
                  >
                    {selectionMode && (
                      <div className="absolute top-2 right-2 z-20 pointer-events-none">
                        {isSelected ? (
                          <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center shadow">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <div className={`w-5 h-5 rounded-full border-2 shadow ${atMax ? 'border-white/30 bg-black/10 opacity-40' : 'border-white/80 bg-black/20'}`} />
                        )}
                      </div>
                    )}
                    <div className={selectionMode && isSelected ? 'ring-2 ring-black h-full' : 'h-full'}>
                      <CertificationCard
                        id={cert.id}
                        name={cert.name || 'Untitled Project'}
                        buildingType={getWeightProfileName(cert)}
                        imageUrl={getMainImage(cert)}
                        siteLocation={`${cert.site_city || ''}${cert.site_city && cert.site_country ? ', ' : ''}${getCountryName(cert.site_country || '')}`}
                        offsiteLocation={`${cert.offsite_city || ''}${cert.offsite_city && cert.offsite_country ? ', ' : ''}${getCountryName(cert.offsite_country || '')}`}
                        status={cert.status}
                        creatorName={cert.creator_name}
                        onSelect={selectionMode && canToggle ? () => toggleSelect(cert.id) : undefined}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <div className="text-center py-16 text-slate-400 italic bg-white border border-slate-200 rounded-lg">
              {isLoading ? 'Loading certifications...' : 'No certifications found.'}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
